import React, { useState } from "react";
import FormStep from "./FormStep";
import Input from "../components/form/Input";
import EmailInput from "../components/form/EmailInput";
import FirmTypeSelect from "../components/form/FirmTypeSelect";
import ConfirmationModal from "../components/ConfirmationModal";

// Configuration: Set to true to make firm type required, false to make it optional
const FIRM_TYPE_REQUIRED = true;

const DelegateDetails = ({ nextStep, prevStep, setFormData, formData }) => {
  const [errors, setErrors] = useState({});
  const [currentDelegate, setCurrentDelegate] = useState({ 
    firstName: "", 
    lastName: "", 
    email: "", 
    telephone: "",
    jobTitle: "",
    companyName: "",
    firmType: ""
  });
  const [isEditing, setIsEditing] = useState(false);
  const [editingIndex, setEditingIndex] = useState(-1);
  const [wasValidated, setWasValidated] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [delegateToDelete, setDelegateToDelete] = useState(-1);
  const [emailValidation, setEmailValidation] = useState({ isChecking: false, hasError: false });
  const [pendingSave, setPendingSave] = useState(false);
  const [pendingNext, setPendingNext] = useState(false);
  
  // Clear email checking errors once validation completes and handle pending operations
  React.useEffect(() => {
    // If we were checking and now we're not, and the form was previously validated, re-validate
    if (!emailValidation.isChecking && wasValidated && errors.email && errors.email.isChecking) {
      // Clear the checking error and re-validate
      setTimeout(() => {
        const isValid = validateForm();
        // If we have a pending save and form is now valid, save the delegate
        if (pendingSave && isValid) {
          setPendingSave(false);
          performSaveDelegate();
        }
        // If we have a pending next and delegates exist, proceed
        if (pendingNext && formData.delegates.length > 0) {
          setPendingNext(false);
          nextStep();
        }
      }, 0);
    }
  }, [emailValidation.isChecking, wasValidated]);
  
  // Helper function to get all existing emails in the form
  const getExistingEmails = () => {
    const emails = [];
    
    // Add primary attendee email if they have a ticket
    if ((formData.bookingFor === "Myself" || formData.bookingFor === "Myself & others") && formData.email) {
      emails.push(formData.email.toLowerCase());
    }
    
    // Add existing delegate emails (excluding the one being edited)
    formData.delegates.forEach((delegate, index) => {
      if (index !== editingIndex && delegate.email) {
        emails.push(delegate.email.toLowerCase());
      }
    });
    
    return emails;
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    // Required fields validation
    if (!currentDelegate.firstName?.trim()) newErrors.firstName = {message: 'First name is required'};
    if (!currentDelegate.lastName?.trim()) newErrors.lastName = {message: 'Last name is required'};
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    
    // Check if email is still being validated or has duplicate error
    if (emailValidation.isChecking) {
      // Show a special error message while checking
      newErrors.email = {message: 'Checking email availability, please wait...', isChecking: true};
    } else if (emailValidation.hasError) {
      // The EmailInput component will show its own duplicate error message
      // We don't need to add an error here, just prevent submission
    } else if (!currentDelegate.email || !emailRegex.test(currentDelegate.email)) {
      // Only show format error if not checking and no duplicate error
      newErrors.email = {message: 'Please enter a valid email address'};
    }
    // If email is valid and not checking and no duplicate error, don't add any error
    
    // Phone validation - allow international formats and require min 11 digits for UK numbers
    const phoneRegex = /^[0-9\s\-\+\(\)]+$/;
    const digitsOnly = currentDelegate.telephone.replace(/[^\d]/g, '');
    
    if (!currentDelegate.telephone) {
      newErrors.telephone = {message: 'Telephone number is required'};
    } else if (!phoneRegex.test(currentDelegate.telephone)) {
      newErrors.telephone = {message: 'Please enter a valid telephone number'};
    } else if (digitsOnly.length < 11) {
      newErrors.telephone = { message: 'Please enter a valid telephone number'};
    }
    
    // Required fields validation for remaining fields
    if (!currentDelegate.jobTitle?.trim()) newErrors.jobTitle = {message: 'Job title is required'};
    if (!currentDelegate.companyName?.trim()) newErrors.companyName = {message: 'Company name is required'};
    
    // Firm type validation - only if required AND firm types are configured
    if (FIRM_TYPE_REQUIRED) {
      const firmTypesConfigured = (() => {
        const formElement = document.getElementById('lde-booking-form');
        if (formElement && formElement.dataset.firmTypes) {
          try {
            const types = JSON.parse(formElement.dataset.firmTypes);
            return types && types.length > 0;
          } catch (e) {
            return false;
          }
        }
        return false;
      })();
      
      if (firmTypesConfigured && !currentDelegate.firmType?.trim()) {
        newErrors.firmType = {message: 'Please select a firm type'};
      }
    }
    
    setErrors(newErrors);
    
    // Check if we should prevent submission due to email validation
    const hasRegularErrors = Object.keys(newErrors).some(key => !newErrors[key]?.isChecking);
    const isEmailValidationBlocking = emailValidation.isChecking || emailValidation.hasError;
    
    return !hasRegularErrors && !isEmailValidationBlocking;
  };

  const performSaveDelegate = () => {
    const updatedDelegates = [...formData.delegates];
    
    if (isEditing) {
      // Update existing delegate
      updatedDelegates[editingIndex] = currentDelegate;
    } else {
      // Add new delegate
      updatedDelegates.push(currentDelegate);
    }
    
    setFormData((prev) => ({ ...prev, delegates: updatedDelegates }));
    resetForm();
  };

  const handleNext = () => {
    if (formData.delegates.length === 0) {
      alert("Please add at least one delegate before proceeding.");
      return;
    }
    
    // If we're in editing mode or entering a new delegate, check if we need to save first
    if (isEditing || currentDelegate.firstName) {
      setWasValidated(true);
      
      // If email is currently being checked, remember that we want to proceed after validation
      if (emailValidation.isChecking) {
        setPendingNext(true);
        setPendingSave(true); // Also save the current delegate if valid
        return;
      }
      
      // If form is not valid, don't proceed
      if (!validateForm()) {
      return;
      }
      
      // Save the delegate and then proceed
      performSaveDelegate();
    }
    
    // Proceed to next step
    nextStep();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCurrentDelegate((prev) => ({ ...prev, [name]: value }));
    
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
    
    // If form was validated before, revalidate on each change
    if (wasValidated) {
      setTimeout(() => {
        validateField(name, value);
      }, 0);
    }
  };
  
  const validateField = (fieldName, value) => {
    let error = null;
    
    switch(fieldName) {
      case 'firstName':
      case 'lastName':
      case 'jobTitle':
      case 'companyName':
        if (!value.trim()) {
          error = {message: `${fieldName.charAt(0).toUpperCase() + fieldName.slice(1).replace(/([A-Z])/g, ' $1').toLowerCase()} is required`};
        }
        break;
      case 'email':
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!value || !emailRegex.test(value)) {
          error = {message: 'Please enter a valid email address'};
        }
        break;
      case 'telephone':
        const phoneRegex = /^[0-9\s\-\+\(\)]+$/;
        const digitsOnly = value.replace(/[^\d]/g, '');
        if (!value) {
          error = {message: 'Telephone number is required'};
        } else if (!phoneRegex.test(value)) {
          error = {message: 'Please enter a valid telephone number'};
        } else if (digitsOnly.length < 11) {
          error = {message: 'Please enter a valid telephone number'};
        }
        break;
      case 'firmType':
        // Only validate if required AND firm types are configured
        if (FIRM_TYPE_REQUIRED) {
          const formElement = document.getElementById('lde-booking-form');
          if (formElement && formElement.dataset.firmTypes) {
            try {
              const types = JSON.parse(formElement.dataset.firmTypes);
              if (types && types.length > 0 && !value?.trim()) {
                error = {message: 'Please select a firm type'};
              }
            } catch (e) {
              // If parsing fails, don't require validation
            }
          }
        }
        break;
      default:
        break;
    }
    
    setErrors(prev => ({
      ...prev,
      [fieldName]: error
    }));
    
    return error === null;
  };
  
  const handleBlur = (e) => {
    const { name, value } = e.target;
    validateField(name, value);
  };

  const saveDelegate = () => {
    setWasValidated(true);
    
    // If email is currently being checked, remember that we want to save
    if (emailValidation.isChecking) {
      setPendingSave(true);
      return;
    }
    
    // If form is not valid, don't save
    if (!validateForm()) {
      return;
    }
    
    // Perform the save
    performSaveDelegate();
  };
  
  const resetForm = () => {
    setCurrentDelegate({ 
      firstName: "", 
      lastName: "", 
      email: "", 
      telephone: "",
      jobTitle: "",
      companyName: "",
      firmType: ""
    });
    setErrors({});
    setIsEditing(false);
    setEditingIndex(-1);
    setWasValidated(false);
    setEmailValidation({ isChecking: false, hasError: false });
  };

  const editDelegate = (index) => {
    // Get the delegate data first
    const delegateToEdit = formData.delegates[index];
    
    // Reset errors before setting new values
    setErrors({});
    
    // Then set the delegate data and editing state
    setCurrentDelegate(delegateToEdit);
    setIsEditing(true);
    setEditingIndex(index);
    setWasValidated(false);
    setEmailValidation({ isChecking: false, hasError: false });
  };

  const handleDeleteClick = (index) => {
    setDelegateToDelete(index);
    setShowDeleteModal(true);
  };
  
  const confirmDelete = () => {
    const updatedDelegates = formData.delegates.filter((_, i) => i !== delegateToDelete);
    setFormData((prev) => ({ ...prev, delegates: updatedDelegates }));
    
    // If we were editing this delegate, reset the form
    if (isEditing && editingIndex === delegateToDelete) {
      resetForm();
    }
    
    // Close the modal
    setShowDeleteModal(false);
  };
  
  const cancelDelete = () => {
    setShowDeleteModal(false);
    setDelegateToDelete(-1);
  };

  const deleteDelegate = (index) => {
    handleDeleteClick(index);
  };
  
  const handleEmailValidationChange = (validationState) => {
    setEmailValidation(validationState);
  };

  return (
    <FormStep 
      title="Delegate Details"
      onBack={prevStep}
      onNext={handleNext}
      isNextDisabled={false}
    >
      <div className="delegate-container">
        
        <div className="delegate-list-column">
          {/* Show primary attendee card if they have a ticket */}
          {(formData.bookingFor === "Myself & others") && (
            <div className="primary-attendee-section">
              <h4>Primary Attendee (You)</h4>
              <div className="delegate-card primary-attendee-card">
                <div className="delegate-info">
                  <div className="delegate-name">{formData.firstName} {formData.lastName}</div>
                  <div className="delegate-email">{formData.email}</div>
                  <div className="delegate-badge">Primary Ticket Holder</div>
                </div>
              </div>
            </div>
          )}
          
          {/* Show additional delegates section */}
          {formData.delegates.length > 0 || formData.bookingFor === "Myself & others" ? (
            <div className="additional-delegates-section">
              <h4>
                {formData.bookingFor === "Myself & others" ? "Additional Delegates" : "Delegates"}
              </h4>
              {formData.delegates.length > 0 ? (
                <div className="delegate-list">
                  {formData.delegates.map((delegate, index) => (
                    <div key={index} className="delegate-card">
                      <div className="delegate-info">
                        <div className="delegate-name">{delegate.firstName} {delegate.lastName}</div>
                        <div className="delegate-email">{delegate.email}</div>
                      </div>
                      <div className="delegate-actions">
                        <button 
                          type="button" 
                          className="edit-btn"
                          onClick={() => editDelegate(index)}
                        >
                          EDIT
                        </button>
                        <button 
                          type="button"
                          className="delete-btn"
                          onClick={() => deleteDelegate(index)}
                        >
                          <i className="icon-trash"></i> DELETE
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="no-delegates">
                  <p>No additional delegates added yet.</p>
                </div>
              )}
            </div>
          ) : (
            <div className="no-delegates">
              <p>No delegates added yet.</p>
            </div>
          )}
        </div>
        <div className="delegate-form-column">
          <div className="form-row">
            <div className="flex">
              <div className="col-6">
                <Input 
                  id="firstName"
                  label="Delegate First Name"
                  name="firstName"
                  autocomplete="given-name"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={currentDelegate.firstName}
                  error={errors.firstName}
                  required={true}
                />
              </div>
              <div className="col-6">
                <Input 
                  id="lastName"
                  label="Delegate Last Name"
                  name="lastName"
                  autocomplete="family-name"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={currentDelegate.lastName}
                  error={errors.lastName}
                  required={true}
                />
              </div>
            </div>
          </div>
          
          <div className="form-row">
            <EmailInput 
              id="email"
              name="email"
              label="Delegate Email"
              autocomplete="email"
              onChange={handleChange}
              onBlur={handleBlur}
              value={currentDelegate.email}
              error={errors.email}
              required={true}
              checkDuplicates={true}
              eventId={formData.event?.id || formData.event?.event_id}
              blogId={formData.event?.blogId || formData.event?.blog_id}
              existingEmails={(() => {
                const emails = [];
                // Add primary attendee email if they have a ticket
                if ((formData.bookingFor === "Myself" || formData.bookingFor === "Myself & others") && formData.email) {
                  emails.push(formData.email.toLowerCase());
                }
                // Add existing delegate emails (excluding the one being edited)
                formData.delegates.forEach((delegate, index) => {
                  if (index !== editingIndex && delegate.email) {
                    emails.push(delegate.email.toLowerCase());
                  }
                });
                return emails;
              })()}
              onValidationChange={handleEmailValidationChange}
            />
          </div>
          
          <div className="form-row">
            <Input 
              id="telephone"
              name="telephone"
              label="Delegate Mobile Number"
              autocomplete="tel"
              onChange={handleChange}
              onBlur={handleBlur}
              value={currentDelegate.telephone}
              error={errors.telephone}
              required={true}
            />
          </div>
          
          <div className="form-row">
            <Input 
              id="jobTitle"
              name="jobTitle"
              label="Delegate Job Title"
              autocomplete="organization-title"
              onChange={handleChange}
              onBlur={handleBlur}
              value={currentDelegate.jobTitle}
              error={errors.jobTitle}
              required={true}
            />
          </div>
          <div className="form-row">
            <Input 
              id="companyName"
              name="companyName"
              label="Company Name (for delegate badge)"
              autocomplete="organization"
              onChange={handleChange}
              onBlur={handleBlur}
              value={currentDelegate.companyName}
              error={errors.companyName}
              required={true}
            />
          </div>
          
          <div className="form-row">
            <FirmTypeSelect
              id="delegateFirmType"
              name="firmType"
              label="Type of Firm"
              onChange={handleChange}
              onBlur={handleBlur}
              value={currentDelegate.firmType}
              error={errors.firmType}
              required={FIRM_TYPE_REQUIRED}
            />
          </div>
          
          <div className="form-row">
            <div className="btn-wrap">
              <button 
                type="button"
                className="btn add-delegate-btn"
                onClick={saveDelegate}
                disabled={formData.delegates.length >= 10 && !isEditing}
              >
                <i className="icon-group"></i> {isEditing ? 'Update Delegate' : 'Add Delegate'}
              </button>
              
              {isEditing && (
                <button 
                  type="button"
                  className="btn cancel-edit-btn"
                  onClick={resetForm}
                >
                  Cancel
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        title="Delete Delegate"
        message="Are you sure you want to remove this delegate?"
      />
    </FormStep>
  );
};

export default DelegateDetails;
